import { CoverTestDataSnapshot, EyeCoverAction, Position } from "../../types/testTypes";

type GazeKey = "leftGazePosition" | "rightGazePosition" | "leftGazeAngles" | "rightGazeAngles";

export interface CoverTestMetrics {
  mean: number;
  stability: number;
}

export interface CoverTestMetricsBundle {
  overall: CoverTestMetrics;
  leftEye: CoverTestMetrics;
  rightEye: CoverTestMetrics;
  isDataModeInDegrees: boolean;
}

type CoverTestResults = Partial<Record<EyeCoverAction, CoverTestDataSnapshot[]>>;

export function isCoverTestDataInDegrees(result: CoverTestResults): boolean {
  return Boolean(
    result[EyeCoverAction.CoverLeftEyeRightKeptOpen]?.some(
      (snapshot) =>
        snapshot?.leftGazeAngles !== undefined || snapshot?.rightGazeAngles !== undefined,
    ),
  );
}

function extractDistances(snapshots: CoverTestDataSnapshot[] | undefined, key: GazeKey): number[] {
  return (snapshots ?? [])
    .map((snapshot) => snapshot?.[key])
    .filter((position): position is Position => Boolean(position))
    .map((position) => Math.sqrt(position.x ** 2 + position.y ** 2));
}

function metricsOf(values: number[]): CoverTestMetrics {
  if (!values.length) return { mean: Number.NaN, stability: Number.NaN };
  const mean = values.reduce((acc, v) => acc + v, 0) / values.length;
  const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / values.length;
  return { mean, stability: Math.sqrt(variance) };
}

/**
 * Reduces a cover-test result into per-eye and overall gaze-offset metrics.
 * Distance = √(x² + y²) per snapshot; stability is the population stdev of
 * those distances across the cover phases.
 */
export function computeCoverTestMetrics(result: CoverTestResults): CoverTestMetricsBundle {
  const isDataModeInDegrees = isCoverTestDataInDegrees(result);
  const leftKey: GazeKey = isDataModeInDegrees ? "leftGazeAngles" : "leftGazePosition";
  const rightKey: GazeKey = isDataModeInDegrees ? "rightGazeAngles" : "rightGazePosition";

  const leftEyeDistances = extractDistances(
    result[EyeCoverAction.CoverRightEyeLeftKeptOpen],
    leftKey,
  );
  const rightEyeDistances = extractDistances(
    result[EyeCoverAction.CoverLeftEyeRightKeptOpen],
    rightKey,
  );

  return {
    overall: metricsOf([...leftEyeDistances, ...rightEyeDistances]),
    leftEye: metricsOf(leftEyeDistances),
    rightEye: metricsOf(rightEyeDistances),
    isDataModeInDegrees,
  };
}
